/** Field-edit dock panel: rename a native-list column's LABEL (Slice 6) + retype it along the safe
 * conversion matrix (Slice 7). The type control is a select of the legal targets for the current
 * type (mirrors the backend matrix); choosing multi_enum from enum is the "make multi-value" move.
 * A multi_enum→enum that would drop data comes back 409 and is surfaced inline. Structured options
 * editing (enum-family) still lives in the Columns panel until Slice 11. */
import { useState } from 'react';
import styles from './crm.module.css';
import { FIELD_TYPE_EXPLAINERS, type EditField } from './CrmColumnHeader';

// current field_type → the field_types it may SAFELY convert to (mirror of lists._CONVERSION_MATRIX).
export const FE_CONVERSION_TARGETS: Record<string, string[]> = {
  text: ['long_text'],
  long_text: ['text'],
  number: ['currency'],
  currency: ['number'],
  enum: ['ranked_enum', 'multi_enum'],
  ranked_enum: ['enum'],
  multi_enum: ['enum'],
};
// only genuinely operator-added fields are retypeable (matches the backend native/template gate).
const RETYPEABLE_ORIGINS = new Set(['native', 'template']);

interface Props {
  field: EditField;
  onSave: (label: string) => void;
  saving: boolean;
  /** Slice 7: retype to a legal target; undefined disables the control. */
  onConvertType?: (newType: string) => void;
  converting?: boolean;
  convertError?: string | null;
}

export function CrmFieldEditPanel({ field, onSave, saving, onConvertType, converting, convertError }: Props) {
  const [label, setLabel] = useState(field.label);
  const trimmed = label.trim();
  const dirty = trimmed !== field.label && trimmed.length > 0;

  const targets = FE_CONVERSION_TARGETS[field.fieldType] ?? [];
  const canRetype = !!onConvertType && RETYPEABLE_ORIGINS.has(field.origin ?? '') && targets.length > 0;

  return (
    <div className={styles.fieldEdit}>
      <label className={styles.fieldEditLabel}>Field name</label>
      <input
        className={styles.setFilterSearch}
        value={label}
        autoFocus
        maxLength={120}
        onChange={(e) => setLabel(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter' && dirty && !saving) onSave(trimmed); }}
      />
      <div className={styles.fieldEditActions}>
        <button className={styles.gridPageBtn} disabled={!dirty || saving} onClick={() => onSave(trimmed)}>
          {saving ? 'Saving…' : 'Save'}
        </button>
      </div>

      <div className={styles.fieldEditTypeRow}>
        <span className={styles.fieldEditTypeLabel}>Type</span>
        {canRetype ? (
          <select
            className={styles.gridGroupBy}
            value={field.fieldType}
            disabled={converting}
            onChange={(e) => { if (e.target.value !== field.fieldType) onConvertType!(e.target.value); }}
          >
            {[field.fieldType, ...targets].map((t) => (
              <option key={t} value={t}>{FIELD_TYPE_EXPLAINERS[t] ?? t}</option>
            ))}
          </select>
        ) : (
          <span className={styles.fieldEditTypeValue}>{FIELD_TYPE_EXPLAINERS[field.fieldType] ?? field.fieldType}</span>
        )}
      </div>
      {convertError && <div className={styles.modalError} role="alert">{convertError}</div>}
      <p className={styles.fieldEditNote}>
        {canRetype
          ? 'Only safe conversions are offered. Options are edited from the Columns panel.'
          : 'This field type can’t be changed. Options are edited from the Columns panel.'}
      </p>
    </div>
  );
}
